/*
 * Server exceptions used to reach end users verbatim. The API's generic
 * `catch (Exception ex) => BadRequest(ex.Message)` puts the raw text into
 * `response.data.message`, and the form views append that to their toast, so a
 * dropped Mongo connection surfaced as a wall of driver internals (cluster id,
 * replica set members, host:port, .NET stack frames) on top of "Submission
 * failed.". Nobody outside the team can act on that, and it advertises the
 * cluster topology to anyone who can trigger an error.
 *
 * shieldResponseError runs inside the single axios response interceptor
 * (commonStore), so every caller is covered without touching the ~35 places
 * that read response.data.message. Only messages that look technical are
 * replaced - validation copy ("Name is required") passes through untouched -
 * and admins/internal users still get the raw text for diagnosis.
 */

const GENERIC_ERROR_MESSAGE =
  "Something went wrong on our side. Please try again, or contact your administrator if the problem continues.";

/* Anything matching these is machine detail, never copy meant for a user. */
const TECHNICAL_PATTERNS = [
  /\b[A-Za-z.]*Exception\b/,
  /\bMongoDB\.Driver\b|\bClusterId\b|\bTopologyVersion\b|\bReplicaSet[A-Za-z]*\b|\bWireVersionRange\b/,
  /\bSystem\.[A-Za-z.]+\b/,
  /(^|\n)\s+at\s+[A-Za-z0-9_.<>`+]+[(.]/,
  /End of inner exception stack trace/i,
  /A timeout occurred after \d+\s*ms/i,
  /No connection could be made|actively refused|connection reset|Timeout expired|The wait operation timed out/i,
  /\b\d{1,3}(?:\.\d{1,3}){3}:\d{2,5}\b/,
  /Traceback \(most recent call last\)/i,
  /\bstack trace\b/i
];

/* Keys that may hold a user-facing message and so get replaced when technical. */
const REPLACE_KEYS = [
  "message", "Message",
  "error", "Error",
  "messageDetail", "MessageDetail",
  "description", "Description"
];

/* Keys that are never user-facing - dropped outright for non-admins. */
const DROP_KEYS = [
  "exceptionMessage", "ExceptionMessage",
  "exceptionType", "ExceptionType",
  "stackTrace", "StackTrace",
  "innerException", "InnerException"
];

/**
 * @param {*} value
 * @returns {boolean} true when the text looks like an exception / stack trace
 */
const isTechnicalErrorMessage = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  TECHNICAL_PATTERNS.some(pattern => pattern.test(value));

/**
 * Scrubs technical detail out of an axios error response, in place.
 *
 * @param {Object} error axios error
 * @param {boolean} canViewDetail true for admin / internal users - left as is
 * @returns {Object} the same error, so callers can chain
 */
const shieldResponseError = (error, canViewDetail) => {
  if (canViewDetail || !error || !error.response) {
    return error;
  }

  const data = error.response.data;

  if (typeof data === "string") {
    if (isTechnicalErrorMessage(data)) {
      error.response.data = GENERIC_ERROR_MESSAGE;
    }
    return error;
  }

  if (!data || typeof data !== "object") {
    return error;
  }

  REPLACE_KEYS.forEach(key => {
    if (isTechnicalErrorMessage(data[key])) {
      data[key] = GENERIC_ERROR_MESSAGE;
    }
  });

  DROP_KEYS.forEach(key => {
    if (key in data) {
      delete data[key];
    }
  });

  /* Field level validation details ride along in `details` - keep the field
   * mapping, scrub only the text. */
  if (Array.isArray(data.details)) {
    data.details.forEach(detail => {
      if (detail && isTechnicalErrorMessage(detail.message)) {
        detail.message = GENERIC_ERROR_MESSAGE;
      }
    });
  }

  return error;
};

export {
  GENERIC_ERROR_MESSAGE,
  isTechnicalErrorMessage,
  shieldResponseError
};
